import { React, useEffect, useState } from "react";
import styled from "styled-components";
import Alerts from "../Alerts/Alerts";
import Departures from "../Departures/Departures";
import CollapsiblePanel from "../CollapsiblePanel/CollapsiblePanel";

const Backdrop = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(13, 12, 29, 0.35);
  opacity: ${({ $isOpen }) => ($isOpen ? 1 : 0)};
  pointer-events: ${({ $isOpen }) => ($isOpen ? "auto" : "none")};
  transition: opacity 300ms cubic-bezier(0.4, 0.0, 0.2, 1);
  z-index: 11;
`

const OffCanvasWrapper = styled.div`
  background-color: #FFFFFF;
  box-shadow: 0 12px 32px rgba(0, 0, 0, 0.18);
  display: flex;
  flex-direction: column;
  height: 100%;
  width: clamp(300px, 26rem, 32vw);
  min-width: 300px;

  border-radius: 0px 0px 25px 0px;
  position: absolute;
  left: 0;
  top: 0;
  transform: ${({$isOpen}) => ($isOpen ? "translateX(0%)" : "translateX(-110%)")};
  transition: transform 300ms cubic-bezier(0.4, 0.0, 0.2, 1);
  z-index: 12;

  @media (max-width: 576px) {
    width: 100%;
    height: 100%;
    justify-content: center;
    top: 0;
  }

  @media (max-height: 500px) {
    width: 35%;
    height: 100%;
    justify-content: center;
    top: 0;
  }
`

const Container = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  grid-template-rows: 30% 1fr auto;
  gap: 0px 0px;
  grid-auto-flow: row;
  grid-template-areas:
    "title"
    "content"
    "close";
  height: 100%;
  overflow: hidden; /* Prevent content from overflowing the container */

  @media (max-height: 500px) {
    grid-template-rows: 80px 1fr auto; /* Adjust Title height for smaller screens */
  }

  @media (max-width: 576px) {
    grid-template-rows: 30% 1fr auto;
  }
`;

const ScrollableContent = styled.div`
  grid-area: content;
  overflow-y: auto; /* Enable vertical scrolling for combined Alerts and Info */
  padding: 16px;
  display: flex;
  flex-direction: column;
  gap: 16px;
  line-height: 1.5;
`;

const Title = styled.div`
  grid-area: title;
  position: relative;
  align-items: flex-end;
  justify-content: flex-start;
  display: flex;
  padding: 20px;
  width: 100%;
  background-image: ${({$stopName}) => ($stopName ? "url(\"/assets/images/" + $stopName.name + ".avif\")" : "")};
  background-size: cover;
  background-position: center;
  text-align: left;
  border-radius: 0px;

  &::after {
    content: "";
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: linear-gradient(to top, rgba(0, 0, 0, 0.8) 0%, rgba(0, 0, 0, 0.35) 45%, rgba(0, 0, 0, 0.05) 100%);
    border-radius: 0px;
  }

  @media (max-height: 500px) {
    padding: 14px;
  }
`

const TitleContent = styled.div`
  position: relative;
  z-index: 1;
`

const Eyebrow = styled.span`
  display: block;
  font-size: 11px;
  font-weight: 600;
  letter-spacing: 0.18em;
  text-transform: uppercase;
  color: rgba(255, 255, 255, 0.75);
  margin-bottom: 4px;
`

const StopLabel = styled.p`
  color: white;
  font-family: "Hammersmith One", sans-serif;
  font-size: 34px;
  line-height: 1.1;
  margin: 0;
  text-shadow: 0 2px 6px rgba(0, 0, 0, 0.6);
  white-space: pre-line;

  @media (max-height: 500px) {
    font-size: 24px;
  }
`

const Info = styled.div`
  width: 100%;
  font-size: 14px;
  color: #4d4d4d;
  font-weight: 400;
  line-height: 1.6;

  p {
    margin: 0 0 12px 0;
  }

  a {
    color: #0d0c1d;
    text-decoration: underline;
    text-underline-offset: 2px;
  }

  #cached {
    margin-top: 16px;
    padding-top: 12px;
    border-top: 1px solid #e6e6e6;
    font-size: 12px;
    color: #8a8a8a;

    a {
      color: #8a8a8a;
    }
  }
`

const Close = styled.button`
  grid-area: close;
  width: 100%;
  height: 48px;
  margin: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  border: none;
  border-radius: 0 0 25px 0;
  font-size: 15px;
  font-family: 'Helvetica Neue', Helvetica, Arial, Sans-Serif;
  font-weight: 600;
  text-transform: uppercase;
  color: #fff;
  cursor: pointer;
  background-color: #fc3a34;
  box-shadow: 0 2px 8px rgba(252, 58, 52, 0.35);
  transition: background 0.2s ease, transform 0.1s ease;

  &:hover,
  &:focus {
    background-color: #d92a24;
    outline: none;
  }

  &:active {
    transform: scale(0.98);
  }

  @media (max-height: 500px) {
    height: 40px;
    font-size: 13px;
  }

  @media (max-width: 576px) {
    height: 56px;
  }
`

const Modal = ({ showModal, closeModal, tramStop }) => {
  const [htmlContent, setHtmlContent] = useState("");
  const [stopName, setStopName] = useState("");
  const [openPanels, setOpenPanels] = useState({
    trams: true,
    alerts: true,
    info: false,
  });

  useEffect(() => { setStopName(tramStop) }, [tramStop]);

  useEffect(() => {
    if (showModal) {
      setOpenPanels({ trams: true, alerts: true, info: false });
    }
  }, [showModal, tramStop]);

  const togglePanel = (key) => {
    setOpenPanels((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  useEffect(() => {
    const handleKeyDown = (event) => {
      if (event.key === "Escape" && showModal) {
        closeModal();
      }
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [showModal, closeModal]);

  useEffect(() => {
        let tramStopName = stopName ? stopName.name : ""
        // Fetch the HTML content from the public directory
        fetch("/assets/html/" + tramStopName + ".html")
        .then((response) => {
          if (!response.ok) {
            throw new Error(`HTTP error! status: ${response.status}`);
          }
          return response.text();
        })
        .then((data) => {
          setHtmlContent(data);
        })
        .catch((error) => {
          console.error("Error fetching HTML content:", error);
        });
    }, [htmlContent, stopName]);

  return (
    <>
      <Backdrop $isOpen={showModal} onClick={() => closeModal()} />
      <OffCanvasWrapper $isOpen={showModal}>
        <Container>
          <Title $stopName={tramStop}>
            <TitleContent>
              <Eyebrow>Metrolink</Eyebrow>
              <StopLabel>
                { tramStop ? tramStop.label : "Error Loading tram info" }
              </StopLabel>
            </TitleContent>
          </Title>
          <ScrollableContent>
            <CollapsiblePanel
              title="Next trams"
              open={openPanels.trams}
              onToggle={() => togglePanel("trams")}
            >
              <Departures tramstop={stopName} open={showModal} />
            </CollapsiblePanel>
            <CollapsiblePanel
              title="Travel alerts"
              open={openPanels.alerts}
              onToggle={() => togglePanel("alerts")}
            >
              <Alerts tramstop={stopName} />
            </CollapsiblePanel>
            <CollapsiblePanel
              title="Station info"
              open={openPanels.info}
              onToggle={() => togglePanel("info")}
            >
              <Info>
                <div dangerouslySetInnerHTML={{ __html: htmlContent }} />
              </Info>
            </CollapsiblePanel>
          </ScrollableContent>
          <Close
            type="button"
            onClick={() => closeModal()}
            data-umami-event="Station details closed"
          >
              Close
          </Close>
        </Container>   
      </OffCanvasWrapper>
    </>
  );
};


export default Modal;
